import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Star, Minus, Plus, Share2, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { getProduct, getRelatedProducts } from '../services/api';
import Notification from '../components/Notification';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('details');
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedSlideIndex, setRelatedSlideIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wantsPrescription, setWantsPrescription] = useState(false);
  const [notification, setNotification] = useState({ isVisible: false, message: '', type: 'success' });
  const { addToCart, closeCart } = useCart();

  // Prescription form state
  const [prescription, setPrescription] = useState({
    rightEye: {
      distance: { sph: '', cyl: '', axis: '' },
      near: { sph: '', cyl: '', axis: '' }
    },
    leftEye: {
      distance: { sph: '', cyl: '', axis: '' },
      near: { sph: '', cyl: '', axis: '' }
    },
    prescriptionFile: null,
    lensType: '',
    lensPrice: 0
  });

  // Prescription options
  const sphOptions = [
    '-6.00', '-5.75', '-5.50', '-5.25', '-5.00', '-4.75', '-4.50', '-4.25', '-4.00', '-3.75',
    '-3.50', '-3.25', '-3.00', '-2.75', '-2.50', '-2.25', '-2.00', '-1.75', '-1.50', '-1.25',
    '-1.00', '-0.75', '-0.50', '-0.25', '0.00', '+0.25', '+0.50', '+0.75', '+1.00', '+1.25',
    '+1.50', '+1.75', '+2.00', '+2.25', '+2.50', '+2.75', '+3.00', '+3.25', '+3.50', '+3.75',
    '+4.00', '+4.25', '+4.50', '+4.75', '+5.00', '+5.25', '+5.50', '+5.75', '+6.00'
  ];

  const cylOptions = [
    '-6.00', '-5.75', '-5.50', '-5.25', '-5.00', '-4.75', '-4.50', '-4.25', '-4.00', '-3.75',
    '-3.50', '-3.25', '-3.00', '-2.75', '-2.50', '-2.25', '-2.00', '-1.75', '-1.50', '-1.25',
    '-1.00', '-0.75', '-0.50', '-0.25', '0.00', '+0.25', '+0.50', '+0.75', '+1.00', '+1.25',
    '+1.50', '+1.75', '+2.00', '+2.25', '+2.50', '+2.75', '+3.00', '+3.25', '+3.50', '+3.75',
    '+4.00', '+4.25', '+4.50', '+4.75', '+5.00', '+5.25', '+5.50', '+5.75', '+6.00'
  ];

  const axisOptions = Array.from({ length: 180 }, (_, i) => (i + 1).toString());

  // Lens-specific SPH options (only negative numbers from -0.5 to -8.0)
  const lensSphOptions = [
    '-0.50', '-0.75', '-1.00', '-1.25', '-1.50', '-1.75', '-2.00',
    '-2.25', '-2.50', '-2.75', '-3.00', '-3.25', '-3.50', '-3.75', '-4.00',
    '-4.25', '-4.50', '-4.75', '-5.00', '-5.25', '-5.50', '-5.75', '-6.00',
    '-6.25', '-6.50', '-6.75', '-7.00', '-7.25', '-7.50', '-7.75', '-8.00'
  ];

  const lensTypes = [
    { name: 'Multicoating', price: 700 },
    { name: 'Multicoating', price: 1400 },
    { name: 'Multicoating', price: 2000 },
    { name: 'Blue Cut', price: 700 },
    { name: 'Blue Cut', price: 1400 },
    { name: 'Blue Cut', price: 2000 },
    { name: 'P/G (Photo Gray)', price: 1400 },
    { name: 'P/G (Photo Gray)', price: 2500 },
    { name: 'Transition', price: 2500 },
    { name: 'Blue Cut + Transition', price: 3000 },
    { name: 'CR Hard Coating', price: 500 },
    { name: 'Super HC (Hard Coat)', price: 900 },
    { name: 'Extra White', price: 1500 }
  ];

  // Fetch product data from Strapi API
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const productData = await getProduct(id);
        
        console.log('Product API Response:', productData);
        
        // Check if product exists
        if (!productData.data || productData.data.length === 0) {
          throw new Error('Product not found');
        }
        
        const productItem = productData.data[0]; // Get first (and only) product from filtered results
        
        // Transform Strapi data
        const quantity = productItem.quantity !== undefined ? productItem.quantity : (productItem.inStock ? 999 : 0);
        const isInStock = quantity > 0 && (productItem.inStock !== false);
        
        // Get category slug from the populated category data
        const categorySlug = productItem.category?.Slug || productItem.category?.slug || '';
        
        const transformedProduct = {
          id: productItem.id,
          name: productItem.Name || productItem.name,
          price: productItem.Price || productItem.price,
          originalPrice: productItem.originalPrice,
          rating: productItem.rating || 5,
          reviewCount: productItem.reviewCount || 0,
          images: [],
          image: '', // Add single image property for cart
          quantity: quantity,
          availability: isInStock ? 'In Stock' : 'Out of Stock',
          description: productItem.Description || productItem.description,
          isOnSale: productItem.isOnSale || false,
          discountPercentage: productItem.discountPercentage || 0,
          offerText: productItem.offerText || '',
          productType: productItem.productType || 'eyeglasses', // Add productType
          category: categorySlug, // Category slug for prescription fee check
          lensOptions: productItem.lensOptions || ['Single Vision', 'Bifocal', 'Progressive', 'Blue Light Filter'],
          careInstructions: productItem.careInstructions || [
            'Clean with microfiber cloth',
            'Store in protective case',
            'Avoid extreme temperatures',
            'Regular professional cleaning recommended'
          ]
        };

        // Process images
        if (productItem.Images && productItem.Images.length > 0) {
          transformedProduct.images = productItem.Images.map(image => {
            if (image.formats && image.formats.large) {
              return image.formats.large.url.startsWith('http') 
                ? image.formats.large.url 
                : `${process.env.REACT_APP_STRAPI_URL}${image.formats.large.url}`;
            } else if (image.url) {
              return image.url.startsWith('http') 
                ? image.url 
                : `${process.env.REACT_APP_STRAPI_URL}${image.url}`;
            }
            return 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
          });
          // Set the first image as the main image for cart
          transformedProduct.image = transformedProduct.images[0];
        } else {
          // Fallback images if no images in Strapi
          transformedProduct.images = [
            'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            'https://images.unsplash.com/photo-1511499767150-a48a237f0083?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
            'https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
          ];
          // Set the first fallback image as the main image for cart
          transformedProduct.image = transformedProduct.images[0];
        }

        setProduct(transformedProduct);

        try {
          const relatedData = await getRelatedProducts(transformedProduct.productType, 20);
          const related = (relatedData.data || [])
            .filter(item => item.id !== transformedProduct.id)
            .map(item => {
              let imageUrl = 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80';

              if (item.Images && item.Images.length > 0) {
                const image = item.Images[0];
                if (image.formats && image.formats.medium) {
                  imageUrl = image.formats.medium.url.startsWith('http')
                    ? image.formats.medium.url
                    : `${process.env.REACT_APP_STRAPI_URL}${image.formats.medium.url}`;
                } else if (image.url) {
                  imageUrl = image.url.startsWith('http')
                    ? image.url
                    : `${process.env.REACT_APP_STRAPI_URL}${image.url}`;
                }
              }

              return {
                id: item.id,
                name: item.Name || item.name,
                price: item.Price || item.price,
                originalPrice: item.originalPrice || item.OriginalPrice || null,
                image: imageUrl,
                category: item.category?.Slug || item.category?.slug || '',
                rating: item.rating || 5,
                reviewCount: item.reviewCount || item.rating || 5,
                quantity: item.quantity !== undefined ? item.quantity : (item.inStock ? 999 : 0),
                isOnSale: item.isOnSale || false,
                discountPercentage: item.discountPercentage || 0,
                isBestSeller: item.isBestseller || item.isBestSeller,
              };
            })
            .sort((first, second) => {
              const firstCategoryMatch = first.category === transformedProduct.category ? 0 : 1;
              const secondCategoryMatch = second.category === transformedProduct.category ? 0 : 1;
              return firstCategoryMatch - secondCategoryMatch;
            })
            .slice(0, 12);

          setRelatedProducts(related);
        } catch (relatedError) {
          console.error('Error fetching related products:', relatedError);
          setRelatedProducts([]);
        }
        
        // Track ViewContent event for Meta Pixel
        if (window.fbq) {
          const productPrice = transformedProduct.price || 0;
          window.fbq('track', 'ViewContent', {
            content_name: transformedProduct.name,
            content_ids: [transformedProduct.id.toString()],
            content_type: 'product',
            value: productPrice,
            currency: 'PKR'
          });
        }
      } catch (err) {
        setError('Failed to load product details');
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id]);


  const reviews = [
    {
      id: 1,
      name: 'Ahmed Khan',
      rating: 5,
      date: '2024-01-15',
      comment: 'Perfect fit and excellent quality. The metal finish is beautiful and durable.',
      verified: true
    },
    {
      id: 2,
      name: 'Fatima Ali',
      rating: 4,
      date: '2024-01-10',
      comment: 'Great glasses, very comfortable. The only minor issue is they slide down a bit.',
      verified: true
    },
    {
      id: 3,
      name: 'Muhammad Hassan',
      rating: 5,
      date: '2024-01-08',
      comment: 'Excellent quality and perfect for my face shape. Highly recommended!',
      verified: true
    }
  ];

  const handleBuyNow = () => {
    handleAddToCart();
    closeCart();
    navigate('/checkout');
  };

  const scrollRelatedProducts = (direction) => {
    const slider = document.getElementById('similar-products-slider');
    if (!slider) return;

    const scrollAmount = slider.clientWidth * 0.85;
    slider.scrollBy({
      left: direction === 'next' ? scrollAmount : -scrollAmount,
      behavior: 'smooth'
    });
  };

  const handleQuantityChange = (change) => {
    if (!product) return;
    
    const newQuantity = quantity + change;
    const maxQuantity = Math.min(product.quantity || 10, 10); // Use product's available quantity or max 10
    
    if (newQuantity >= 1 && newQuantity <= maxQuantity) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;

    // Check stock availability
    const availableQuantity = product.quantity || 0;
    if (availableQuantity <= 0) {
      setNotification({
        isVisible: true,
        message: 'Sorry, this product is out of stock!',
        type: 'error'
      });
      return;
    }

    // Check if requested quantity exceeds available stock
    if (quantity > availableQuantity) {
      setNotification({
        isVisible: true,
        message: `Only ${availableQuantity} item(s) available in stock!`,
        type: 'error'
      });
      setQuantity(availableQuantity);
      return;
    }

    const prescriptionFee = wantsPrescription ? getPrescriptionFee() : 0;
    const productWithLens = {
      ...product,
      price: product.price + (wantsPrescription ? prescription.lensPrice : 0) + prescriptionFee,
      lensType: wantsPrescription ? prescription.lensType : null,
      lensPrice: wantsPrescription ? prescription.lensPrice : 0,
      prescriptionFee: prescriptionFee,
      prescription: wantsPrescription ? prescription : null
    };
    addToCart(productWithLens, quantity);
    
    // Track AddToCart event for Meta Pixel
    if (window.fbq) {
      const totalValue = productWithLens.price * quantity;
      window.fbq('track', 'AddToCart', {
        content_name: product.name,
        content_ids: [product.id.toString()],
        content_type: 'product',
        value: totalValue,
        currency: 'PKR',
        num_items: quantity
      });
    }
    
    setNotification({
      isVisible: true,
      message: `${product.name} has been added to your cart successfully!`,
      type: 'success'
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, isVisible: false }));
  };

  // Prescription form handlers
  const handlePrescriptionChange = (eye, vision, field, value) => {
    setPrescription(prev => ({
      ...prev,
      [eye]: {
        ...prev[eye],
        [vision]: {
          ...prev[eye][vision],
          [field]: value
        }
      }
    }));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPrescription(prev => ({
        ...prev,
        prescriptionFile: file
      }));
    }
  };

  const handleLensTypeChange = (value) => {
    const selectedLens = lensTypes.find(lens => `${lens.name} - Rs. ${lens.price}` === value);
    setPrescription(prev => ({
      ...prev,
      lensType: value,
      lensPrice: selectedLens ? selectedLens.price : 0
    }));
  };

  // Prescription fee for DAHAB and SIGMA categories
  const PRESCRIPTION_FEE = 300;
  
  // Check if prescription fee applies (for DAHAB and SIGMA categories)
  const prescriptionFeeApplies = () => {
    if (!product) return false;
    return wantsPrescription && (product.category === 'dahab' || product.category === 'sigma') && hasPrescriptionData();
  };
  
  // Get prescription fee amount
  const getPrescriptionFee = () => {
    return prescriptionFeeApplies() ? PRESCRIPTION_FEE : 0;
  };

  // Check if any prescription field is filled
  const hasPrescriptionData = () => {
    if (!wantsPrescription) {
      return false;
    }

    const rightEye = prescription.rightEye;
    const leftEye = prescription.leftEye;
    
    // Check if prescription file is uploaded
    if (prescription.prescriptionFile) {
      return true;
    }
    
    // For eyeglasses: check SPH, CYL, AXIS
    if (product && product.productType === 'eyeglasses') {
      const rightDistance = rightEye.distance.sph || rightEye.distance.cyl || rightEye.distance.axis;
      const rightNear = rightEye.near.sph || rightEye.near.cyl || rightEye.near.axis;
      const leftDistance = leftEye.distance.sph || leftEye.distance.cyl || leftEye.distance.axis;
      const leftNear = leftEye.near.sph || leftEye.near.cyl || leftEye.near.axis;
      
      return rightDistance || rightNear || leftDistance || leftNear;
    }
    
    // For lenses: check only Distance SPH
    if (product && product.productType === 'lenses') {
      const rightDistance = rightEye.distance.sph;
      const leftDistance = leftEye.distance.sph;
      
      return rightDistance || leftDistance;
    }
    
    return false;
  };

  // Check if add to cart button should be disabled
  const isAddToCartDisabled = () => {
    // Check if product is out of stock
    if (product && (product.quantity === 0 || (product.quantity === undefined && !product.inStock))) {
      return true;
    }
    
    // If the customer does not want prescription, no prescription fields are required.
    if (!wantsPrescription) {
      return false;
    }
    
    // For eyeglasses: If user has filled prescription fields, they must select lens type
    if (hasPrescriptionData() && !prescription.lensType) {
      return true;
    }
    return false;
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 text-lg">{error || 'Product not found'}</p>
          <Link 
            to="/eyeglasses"
            className="mt-4 bg-primary text-white px-4 py-2 rounded-lg inline-block"
          >
            Back to Eyeglasses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{product.name} - Mughal Ainak Mahal | Mughalpura, Lahore</title>
        <meta name="description" content={`${product.name} - ${product.description}. Available at Mughal Ainak Mahal in Mughalpura, Lahore.`} />
      </Helmet>
      
      <Notification
        isVisible={notification.isVisible}
        message={notification.message}
        type={notification.type}
        onClose={handleCloseNotification}
      />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex text-sm text-gray-500">
            <Link to="/" className="hover:text-primary">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/eyeglasses" className="hover:text-primary">Eyeglasses</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left Column - Images */}
          <div className="space-y-4 lg:sticky lg:top-20 lg:self-start">
            {/* Main Image */}
            <div className="relative group">
              <img
                src={product.images[selectedImage]}
                alt={product.name}
                className="w-full h-96 lg:h-[500px] object-cover rounded-lg cursor-zoom-in transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80';
                }}
              />
              <div className="absolute top-4 right-4 flex space-x-2">
                <button
                  onClick={() => setIsWishlisted(!isWishlisted)}
                  className={`p-2 rounded-full ${
                    isWishlisted ? 'bg-red-500 text-white' : 'bg-white text-gray-600'
                  } hover:bg-red-500 hover:text-white transition-colors duration-200`}
                >
                  <Heart className="w-5 h-5" />
                </button>
                <button className="p-2 rounded-full bg-white text-gray-600 hover:bg-gray-100 transition-colors duration-200">
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
              {product.isOnSale && (
                <div className="absolute left-px top-px bg-red-500 text-white px-2 py-0.5 text-[11px] font-semibold rounded-tl-md rounded-br-md sm:px-3 sm:py-1 sm:text-sm">
                  {product.discountPercentage}% OFF
                </div>
              )}
            </div>

            {/* Thumbnail Images */}
            <div className="flex space-x-2 overflow-x-auto">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors duration-200 ${
                    selectedImage === index ? 'border-primary' : 'border-gray-200'
                  }`}
                >
                  <img
                    src={image}
                    alt={`${product.name} - View ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80';
                    }}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Right Column - Product Info */}
          <div className="space-y-6">
            {/* Product Name */}
            <h1 className="text-3xl lg:text-4xl font-bold text-secondary">{product.name}</h1>

            {/* Rating */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i < Math.floor(product.rating)
                        ? 'text-yellow-400 fill-current'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-gray-600">({product.reviewCount} reviews)</span>
            </div>

            {/* Price */}
            <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-3">
              <span className="text-3xl font-bold text-primary">
                  Rs. {(product.price + (wantsPrescription ? prescription.lensPrice : 0) + getPrescriptionFee()).toLocaleString()}
              </span>
              </div>
              {wantsPrescription && (prescription.lensPrice > 0 || getPrescriptionFee() > 0) && (
                <div className="flex flex-col space-y-1 text-sm text-gray-600">
              {prescription.lensPrice > 0 && (
                    <span>
                      Frame: Rs. {product.price.toLocaleString()} + Lens: Rs. {prescription.lensPrice.toLocaleString()}
                </span>
                  )}
                  {getPrescriptionFee() > 0 && (
                    <span className="text-primary font-medium">
                      + Prescription Fee: Rs. {getPrescriptionFee().toLocaleString()}
                    </span>
                  )}
                </div>
              )}
              {product.originalPrice && product.originalPrice > product.price && (
                <span className="text-xl text-gray-500 line-through">
                  Rs. {product.originalPrice.toLocaleString()}
                </span>
              )}
              {product.isOnSale && product.discountPercentage > 0 && (
                <span className="bg-red-100 text-red-600 px-2 py-1 rounded text-sm font-medium">
                  {product.discountPercentage}% OFF
                </span>
              )}
            </div>

            {/* Offer Text */}
            {product.offerText && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-yellow-800 font-medium">{product.offerText}</p>
              </div>
            )}

            {/* Availability & Stock */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${
                  product.availability === 'In Stock' ? 'bg-green-500' : 'bg-red-500'
                }`}></div>
                <span className="text-gray-600">{product.availability}</span>
              </div>
              {product.quantity !== undefined && product.quantity > 0 && (
                <p className="text-sm text-gray-500">
                  {product.quantity} {product.quantity === 1 ? 'item' : 'items'} available in stock
                </p>
              )}
              {product.quantity !== undefined && product.quantity === 0 && (
                <p className="text-sm text-red-600 font-medium">Out of stock</p>
              )}
            </div>

            {/* Quantity */}
            <div>
              <h3 className="text-lg font-semibold text-secondary mb-3">Quantity</h3>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => handleQuantityChange(-1)}
                  className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-primary transition-colors duration-200"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-xl font-semibold w-12 text-center">{quantity}</span>
                <button
                  onClick={() => handleQuantityChange(1)}
                  className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-primary transition-colors duration-200"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Prescription Fee Notice for DAHAB and SIGMA */}
            {wantsPrescription && product && (product.category === 'dahab' || product.category === 'sigma') && (
              <div className="bg-gray-50 border border-gray-200 p-4 rounded-lg">
                <p className="text-sm text-gray-800">
                  Prescription Fee: Rs. {PRESCRIPTION_FEE} will be added when you add a prescription for {product.category.toUpperCase()} products.
                </p>
              </div>
            )}

            {product && (product.productType === 'eyeglasses' || product.productType === 'lenses') && (
              <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:border-primary transition-colors duration-200">
                <input
                  type="checkbox"
                  checked={wantsPrescription}
                  onChange={(event) => setWantsPrescription(event.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span>
                  <span className="block text-base font-semibold text-secondary">
                    Add prescription details
                  </span>
                  <span className="mt-1 block text-sm text-gray-600">
                    Check this if you want to enter your eyesight prescription or upload a prescription file.
                  </span>
                </span>
              </label>
            )}

            {/* Prescription Card - Show for eyeglasses */}
            {wantsPrescription && product && product.productType === 'eyeglasses' && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 space-y-6">

              {/* Right Eye Prescription */}
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3">Add Prescription - Right (Optional):</h3>
                <div className="overflow-x-auto border border-gray-300 rounded-lg">
                  <table className="w-full text-xs md:text-sm">
                    <thead>
                      <tr className="bg-gray-600 text-white">
                        <th className="px-2 md:px-4 py-2 md:py-3 text-center font-medium"></th>
                        <th className="px-2 md:px-4 py-2 md:py-3 text-center font-medium">SPH</th>
                        <th className="px-2 md:px-4 py-2 md:py-3 text-center font-medium">CYL</th>
                        <th className="px-2 md:px-4 py-2 md:py-3 text-center font-medium">AXIS</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-2 md:px-4 py-2 md:py-3 font-medium text-gray-800">Distance</td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.rightEye.distance.sph}
                            onChange={(e) => handlePrescriptionChange('rightEye', 'distance', 'sph', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm appearance-none"
                            style={{ WebkitAppearance: 'menulist', MozAppearance: 'menulist' }}
                          >
                            <option value="">-</option>
                            {sphOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.rightEye.distance.cyl}
                            onChange={(e) => handlePrescriptionChange('rightEye', 'distance', 'cyl', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                          >
                            <option value="">-</option>
                            {cylOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.rightEye.distance.axis}
                            onChange={(e) => handlePrescriptionChange('rightEye', 'distance', 'axis', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                          >
                            <option value="">-</option>
                            {axisOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-2 md:px-4 py-2 md:py-3 font-medium text-gray-800">Near</td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.rightEye.near.sph}
                            onChange={(e) => handlePrescriptionChange('rightEye', 'near', 'sph', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                          >
                            <option value="">-</option>
                            {sphOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.rightEye.near.cyl}
                            onChange={(e) => handlePrescriptionChange('rightEye', 'near', 'cyl', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                          >
                            <option value="">-</option>
                            {cylOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.rightEye.near.axis}
                            onChange={(e) => handlePrescriptionChange('rightEye', 'near', 'axis', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                          >
                            <option value="">-</option>
                            {axisOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Left Eye Prescription */}
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3">Add Prescription - Left (Optional):</h3>
                <div className="overflow-x-auto border border-gray-300 rounded-lg">
                  <table className="w-full text-xs md:text-sm">
                    <thead>
                      <tr className="bg-gray-600 text-white">
                        <th className="px-2 md:px-4 py-2 md:py-3 text-center font-medium"></th>
                        <th className="px-2 md:px-4 py-2 md:py-3 text-center font-medium">SPH</th>
                        <th className="px-2 md:px-4 py-2 md:py-3 text-center font-medium">CYL</th>
                        <th className="px-2 md:px-4 py-2 md:py-3 text-center font-medium">AXIS</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-2 md:px-4 py-2 md:py-3 font-medium text-gray-800">Distance</td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.leftEye.distance.sph}
                            onChange={(e) => handlePrescriptionChange('leftEye', 'distance', 'sph', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                          >
                            <option value="">-</option>
                            {sphOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.leftEye.distance.cyl}
                            onChange={(e) => handlePrescriptionChange('leftEye', 'distance', 'cyl', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                          >
                            <option value="">-</option>
                            {cylOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.leftEye.distance.axis}
                            onChange={(e) => handlePrescriptionChange('leftEye', 'distance', 'axis', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                          >
                            <option value="">-</option>
                            {axisOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-2 md:px-4 py-2 md:py-3 font-medium text-gray-800">Near</td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.leftEye.near.sph}
                            onChange={(e) => handlePrescriptionChange('leftEye', 'near', 'sph', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                          >
                            <option value="">-</option>
                            {sphOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.leftEye.near.cyl}
                            onChange={(e) => handlePrescriptionChange('leftEye', 'near', 'cyl', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                          >
                            <option value="">-</option>
                            {cylOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.leftEye.near.axis}
                            onChange={(e) => handlePrescriptionChange('leftEye', 'near', 'axis', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                          >
                            <option value="">-</option>
                            {axisOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* File Upload */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Upload Prescription :</h3>
                <div className="flex items-center space-x-4">
                  <input
                    type="file"
                    id="prescription-file"
                    onChange={handleFileUpload}
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                  />
                  <label
                    htmlFor="prescription-file"
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded cursor-pointer transition-colors duration-200"
                  >
                    Choose File
                  </label>
                  <span className="text-gray-600">
                    {prescription.prescriptionFile ? prescription.prescriptionFile.name : 'No file chosen'}
                  </span>
                </div>
              </div>

              {/* Lens Type Selection */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                  Select Lens Type:
                  {!prescription.lensType && (
                    <span className="ml-2 text-red-500 text-lg"></span>
                  )}
                </h3>
                <div className="relative">
                  <select
                    value={prescription.lensType}
                    onChange={(e) => handleLensTypeChange(e.target.value)}
                    className={`w-full px-3 py-2 border rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      !prescription.lensType ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select lens type</option>
                    {lensTypes.map((lens, index) => (
                      <option key={index} value={`${lens.name} - Rs. ${lens.price}`}>
                        {lens.name} - Rs. {lens.price}
                      </option>
                    ))}
                  </select>
                  {!prescription.lensType && (
                    <span className="absolute right-8 top-1/2 transform -translate-y-1/2 text-red-500"></span>
                  )}
                </div>
              </div>
            </div>
            )}


            {/* Prescription Card - Show for lenses */}
            {wantsPrescription && product && product.productType === 'lenses' && (
            <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 space-y-6">

              {/* Right Eye Prescription */}
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3">Add Prescription - Right (Optional):</h3>
                <div className="overflow-x-auto border border-gray-300 rounded-lg">
                  <table className="w-full text-xs md:text-sm">
                    <thead>
                      <tr className="bg-gray-600 text-white">
                        <th className="px-2 md:px-4 py-2 md:py-3 text-center font-medium"></th>
                        <th className="px-2 md:px-4 py-2 md:py-3 text-center font-medium">SPH</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-2 md:px-4 py-2 md:py-3 font-medium text-gray-800">Distance</td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.rightEye.distance.sph}
                            onChange={(e) => handlePrescriptionChange('rightEye', 'distance', 'sph', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm appearance-none"
                            style={{ WebkitAppearance: 'menulist', MozAppearance: 'menulist' }}
                          >
                            <option value="">-</option>
                            {lensSphOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Left Eye Prescription */}
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3">Add Prescription - Left (Optional):</h3>
                <div className="overflow-x-auto border border-gray-300 rounded-lg">
                  <table className="w-full text-xs md:text-sm">
                    <thead>
                      <tr className="bg-gray-600 text-white">
                        <th className="px-2 md:px-4 py-2 md:py-3 text-center font-medium"></th>
                        <th className="px-2 md:px-4 py-2 md:py-3 text-center font-medium">SPH</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-2 md:px-4 py-2 md:py-3 font-medium text-gray-800">Distance</td>
                        <td className="px-2 md:px-4 py-2 md:py-3">
                          <select
                            value={prescription.leftEye.distance.sph}
                            onChange={(e) => handlePrescriptionChange('leftEye', 'distance', 'sph', e.target.value)}
                            className="w-full px-2 md:px-3 py-1 md:py-2 border border-gray-300 rounded bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs md:text-sm"
                          >
                            <option value="">-</option>
                            {lensSphOptions.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            )}

            {/* CTA Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleBuyNow}
                disabled={isAddToCartDisabled()}
                className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 ${
                  isAddToCartDisabled()
                    ? 'border border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed'
                    : 'border border-black bg-white text-black hover:bg-black hover:text-white active:bg-black active:text-white'
                }`}
              >
                Buy Now
              </button>
              <button
                onClick={handleAddToCart}
                disabled={isAddToCartDisabled()}
                className={`w-full py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 ${
                  isAddToCartDisabled() 
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                    : 'bg-black text-white hover:bg-primary hover:shadow-lg'
                }`}
              >
                Add to Cart
              </button>
              {isAddToCartDisabled() && (
                <p className="text-red-500 text-sm text-center">
                  {product && (product.quantity === 0 || (product.quantity === undefined && !product.inStock))
                    ? 'This product is out of stock'
                    : 'Please select a lens type to continue with prescription'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="mt-16">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8">
              {[
                { id: 'details', name: 'Description' },
                ...(product && product.productType === 'eyeglasses' ? [{ id: 'lens', name: 'Lens Options' }] : []),
                { id: 'reviews', name: 'Reviews' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="py-8">
            {activeTab === 'details' && (
              <div className="max-w-4xl">
                <h3 className="text-xl font-semibold text-secondary mb-4">Description</h3>
                <div className="text-gray-600 leading-relaxed text-lg whitespace-pre-line">
                  {product.description}
                </div>
              </div>
            )}

            {activeTab === 'lens' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold text-secondary mb-4">Available Lens Options</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Multicoating */}
                    <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
                      <h4 className="font-semibold text-secondary text-lg mb-2">Multicoating</h4>
                      <p className="text-gray-600 text-sm mb-3">Advanced multi-layer coating that provides superior scratch resistance, anti-reflective properties, and easy cleaning.</p>
                      <div className="text-primary font-semibold">Rs. 700 - 2000</div>
                    </div>

                    {/* Blue Cut */}
                    <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
                      <h4 className="font-semibold text-secondary text-lg mb-2">Blue Cut</h4>
                      <p className="text-gray-600 text-sm mb-3">Special coating that filters harmful blue light from digital screens, reducing eye strain and improving sleep quality.</p>
                      <div className="text-primary font-semibold">Rs. 700 - 2000</div>
                    </div>

                    {/* P/G (Photo Gray) */}
                    <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
                      <h4 className="font-semibold text-secondary text-lg mb-2">P/G (Photo Gray)</h4>
                      <p className="text-gray-600 text-sm mb-3">Photochromic lenses that automatically darken in sunlight and lighten indoors, providing UV protection and comfort.</p>
                      <div className="text-primary font-semibold">Rs. 1400 - 2500</div>
                    </div>

                    {/* Transition */}
                    <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
                      <h4 className="font-semibold text-secondary text-lg mb-2">Transition</h4>
                      <p className="text-gray-600 text-sm mb-3">Premium photochromic technology that adapts to light conditions, offering clear vision indoors and protection outdoors.</p>
                      <div className="text-primary font-semibold">Rs. 2500</div>
                    </div>

                    {/* Blue Cut + Transition */}
                    <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
                      <h4 className="font-semibold text-secondary text-lg mb-2">Blue Cut + Transition</h4>
                      <p className="text-gray-600 text-sm mb-3">Combines blue light filtering with photochromic technology for maximum protection against digital eye strain and UV rays.</p>
                      <div className="text-primary font-semibold">Rs. 3000</div>
                    </div>

                    {/* CR Hard Coating */}
                    <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
                      <h4 className="font-semibold text-secondary text-lg mb-2">CR Hard Coating</h4>
                      <p className="text-gray-600 text-sm mb-3">Basic hard coating that provides scratch resistance and durability for everyday use at an affordable price.</p>
                      <div className="text-primary font-semibold">Rs. 500</div>
                    </div>

                    {/* Super HC (Hard Coat) */}
                    <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
                      <h4 className="font-semibold text-secondary text-lg mb-2">Super HC (Hard Coat)</h4>
                      <p className="text-gray-600 text-sm mb-3">Enhanced hard coating with superior scratch resistance and durability, perfect for active lifestyles.</p>
                      <div className="text-primary font-semibold">Rs. 900</div>
                    </div>

                    {/* Extra White */}
                    <div className="p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
                      <h4 className="font-semibold text-secondary text-lg mb-2">Extra White</h4>
                      <p className="text-gray-600 text-sm mb-3">Premium lens material with exceptional clarity and whiteness, providing the clearest possible vision.</p>
                      <div className="text-primary font-semibold">Rs. 1500</div>
                      </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-secondary mb-4">Care Instructions</h3>
                  <ul className="space-y-2">
                    {product.careInstructions.map((instruction, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-primary mr-2">•</span>
                        <span className="text-gray-600">{instruction}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-secondary">Customer Reviews</h3>
                  <button className="text-primary hover:text-primary-dark font-medium">
                    Write a Review
                  </button>
                </div>
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b border-gray-200 pb-6">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold text-secondary">{review.name}</span>
                          {review.verified && (
                            <span className="text-green-500 text-sm">✓ Verified</span>
                          )}
                        </div>
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < review.rating
                                  ? 'text-yellow-400 fill-current'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-600 mb-2">{review.comment}</p>
                      <span className="text-sm text-gray-500">{review.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {relatedProducts.length > 0 && (
        <section className="mt-12">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">You May Also Like</p>
              <h2 className="mt-2 text-2xl font-bold text-secondary md:text-3xl">Similar Glasses</h2>
            </div>
            <Link
              to={product.productType === 'sunglasses' ? '/sunglasses' : product.productType === 'kids' ? '/kids' : product.productType === 'lenses' || product.productType === 'dahab' || product.productType === 'sigma' || product.productType === 'comfort' ? '/lenses' : '/eyeglasses'}
              className="hidden rounded-md border border-primary px-5 py-2 text-sm font-semibold text-primary transition hover:bg-primary hover:text-white md:inline-flex"
            >
              View all
            </Link>
          </div>

          <div className="relative bg-white px-8 py-4 md:px-12">
            <button
              type="button"
              onClick={() => scrollRelatedProducts('prev')}
              className="absolute left-0 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded bg-gray-100 text-secondary shadow transition hover:bg-primary hover:text-white md:-left-2"
              aria-label="Previous similar products"
            >
              <ChevronLeft size={22} />
            </button>

            <div
              id="similar-products-slider"
              onScroll={(event) => {
                const nextIndex = Math.round(event.currentTarget.scrollLeft / event.currentTarget.clientWidth);
                setRelatedSlideIndex(nextIndex);
              }}
              className="mx-auto flex max-w-6xl snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth pb-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {relatedProducts.map((related) => {
                const relatedPath = `/product/${related.id}`;

                return (
                  <div
                    key={related.id}
                    className="group flex h-full w-[82vw] flex-none snap-center flex-col overflow-hidden rounded-lg bg-white shadow-md transition duration-300 hover:-translate-y-1 hover:shadow-xl sm:w-[310px] md:shadow-lg lg:w-[calc((100%_-_3rem)/3)]"
                  >
                    <Link to={relatedPath} className="block">
                      <div className="relative aspect-[1.08/1] overflow-hidden bg-white md:aspect-[4/3]">
                        <img
                          src={related.image}
                          alt={related.name}
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        />
                        {related.isOnSale && related.discountPercentage > 0 && (
                          <div className="absolute left-px top-px bg-red-500 px-2 py-0.5 text-[11px] font-semibold text-white rounded-tl-md rounded-br-md sm:px-3 sm:py-1 sm:text-sm">
                            {related.discountPercentage}% OFF
                          </div>
                        )}
                        {related.isBestSeller && (
                          <div className="absolute right-px top-px bg-yellow-500 px-1.5 py-0.5 text-[10px] font-semibold text-white rounded-tr-md rounded-bl-md sm:px-3 sm:py-1 sm:text-sm">
                            Best Seller
                          </div>
                        )}
                      </div>
                    </Link>

                    <div className="flex flex-1 flex-col px-2 pb-2 pt-1.5 text-center md:px-5 md:pb-5 md:pt-3">
                      <Link to={relatedPath} className="block">
                        <h3 className="line-clamp-2 min-h-[24px] text-[12px] font-bold uppercase leading-tight tracking-[0.04em] text-secondary transition hover:text-primary sm:text-sm md:min-h-[38px] md:text-base md:tracking-[0.08em]">
                          {related.name}
                        </h3>
                      </Link>

                      <div className="mt-0.5 flex min-h-[16px] flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-xs md:mt-1.5 md:min-h-[22px] md:gap-2 md:text-base">
                        {related.originalPrice && related.originalPrice > related.price && (
                          <span className="text-gray-500 line-through">Rs. {related.originalPrice.toLocaleString()}</span>
                        )}
                        <span className="font-bold text-primary">Rs. {related.price?.toLocaleString()}</span>
                      </div>

                      <div className="mt-1 flex min-h-[14px] items-center justify-center gap-1 text-[10px] leading-none text-gray-600 md:mt-2 md:min-h-[18px] md:text-sm">
                        <span className="tracking-tight text-yellow-400">★★★★★</span>
                        <span>{related.reviewCount || related.rating || 5} reviews</span>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          addToCart({
                            ...related,
                            quantity: 1,
                            lensPrice: 0,
                            finalPrice: related.price,
                            maxStock: related.quantity || null,
                          })
                        }
                        className="mt-1 w-full rounded-md bg-primary px-2 py-2 text-xs font-bold uppercase tracking-[0.06em] text-white transition hover:bg-primary-dark md:mt-2 md:px-4 md:py-3 md:text-base md:tracking-[0.18em]"
                      >
                        Add to Cart
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => scrollRelatedProducts('next')}
              className="absolute right-0 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded bg-gray-100 text-secondary shadow transition hover:bg-primary hover:text-white md:-right-2"
              aria-label="Next similar products"
            >
              <ChevronRight size={22} />
            </button>
          </div>

          <div className="mt-3 flex justify-center gap-2">
            {Array.from({ length: Math.max(Math.ceil(relatedProducts.length / 3), 1) }).map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  const slider = document.getElementById('similar-products-slider');
                  if (slider) {
                    slider.scrollTo({ left: slider.clientWidth * index, behavior: 'smooth' });
                  }
                  setRelatedSlideIndex(index);
                }}
                className={`h-2.5 w-2.5 rounded-full transition ${
                  relatedSlideIndex === index ? 'w-6 bg-primary' : 'bg-gray-300 hover:bg-primary/60'
                }`}
                aria-label={`Go to similar products slide ${index + 1}`}
              />
            ))}
          </div>
        </section>
      )}
      </div>
    </div>
  );
};

export default ProductDetail;
